import type { CityConfig } from "@/pages/city-template/types";

export const norwalkConfig: CityConfig = {
  city: "Norwalk",
  state: "Connecticut",
  stateAbbr: "CT",
  slug: "norwalk",
  route: "/norwalk",
  title: "Expedited Passport Services in Norwalk, CT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Norwalk, CT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Norwalk, Westport, Darien, Wilton, and all of Fairfield County.",
  h1: "Expedited Passport Services in Norwalk, CT",
  heroSubtitle:
    "Norwalk travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No need to visit the Connecticut Passport Agency.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20Norwalk%20Connecticut%20harbor%20with%20the%20Maritime%20Aquarium%20and%20oyster%20boats%20at%20golden%20hour%2C%20waterfront%20cityscape%20with%20colorful%20buildings%20along%20the%20Long%20Island%20Sound%2C%20warm%20amber%20lighting%2C%20New%20England%20coastal%20charm%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=ct-nrw-hero-001&orientation=landscape",
  heroImageAlt: "Norwalk Connecticut harbor at golden hour",
  geoPosition: "41.1177; -73.4082",
  geoPlacename: "Norwalk, Connecticut, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Connecticut Passport Agency",
    address: "850 Canal Street, Stamford, CT 06902",
    distance: "~45 miles from Norwalk",
  },
  nearbyCitiesZips: [
    { name: "Norwalk", zips: "06850, 06851, 06852, 06853, 06854, 06855, 06856, 06857, 06859, 06860" },
    { name: "Westport", zips: "06880, 06881, 06888, 06889" },
    { name: "Darien", zips: "06820" },
    { name: "Wilton", zips: "06897" },
    { name: "New Canaan", zips: "06840" },
    { name: "Stamford", zips: "06901, 06902, 06903, 06904, 06905, 06906, 06907, 06910, 06911, 06912, 06914, 06920, 06921, 06925, 06926, 06927" },
    { name: "Greenwich", zips: "06830, 06831, 06832, 06836" },
    { name: "Fairfield", zips: "06824, 06825, 06828" },
    { name: "Bridgeport", zips: "06601, 06602, 06604, 06605, 06606, 06607, 06608, 06610, 06650, 06673, 06699" },
    { name: "Stratford", zips: "06614, 06615" },
    { name: "Trumbull", zips: "06611" },
    { name: "Shelton", zips: "06484, 06485" },
    { name: "Ridgefield", zips: "06877, 06879" },
    { name: "Danbury", zips: "06810, 06811, 06813, 06814, 06816, 06817" },
    { name: "Bethel", zips: "06801" },
    { name: "Brookfield", zips: "06804" },
    { name: "New Fairfield", zips: "06812" },
    { name: "Redding", zips: "06875, 06896" },
    { name: "Weston", zips: "06883" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Norwalk travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Fairfield County",
      desc: "Norwalk, Westport, Darien, Wilton, New Canaan, and all Fairfield County communities.",
    },
  ],
  testimonials: [
    {
      name: "Jennifer A.",
      location: "Norwalk, CT",
      rating: 5,
      text: "I needed my passport renewed for a business trip to Paris in 5 days. Fast Passport Center delivered it in 3 days. Their Norwalk service is excellent.",
      service: "Passport Renewal",
    },
    {
      name: "Michael T.",
      location: "Westport, CT",
      rating: 5,
      text: "Applied for my daughter's first passport for a family trip to Japan. Fast Passport Center handled the child passport process and had it ready in 4 days. Westport families trust them.",
      service: "Child Passport",
    },
    {
      name: "Lisa M.",
      location: "Darien, CT",
      rating: 5,
      text: "Lost my passport before a cruise. Fast Passport Center replaced it in under 48 hours. Darien residents can count on them for emergencies.",
      service: "Lost Passport",
    },
    {
      name: "Robert K.",
      location: "Wilton, CT",
      rating: 5,
      text: "Needed a name change after divorce. Fast Passport Center handled the paperwork and I had my updated passport in 4 days. Professional Wilton-area service.",
      service: "Name Change",
    },
  ],
  facilities: [
    {
      id: "nrw-1",
      name: "Norwalk Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "16 Trinity Place",
      city: "Norwalk",
      state: "CT",
      zip: "06850",
      phone: "203-866-6511",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Norwalk",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "nrw-2",
      name: "Norwalk City Hall",
      type: "City Clerk / Passport Services",
      address: "125 East Avenue",
      city: "Norwalk",
      state: "CT",
      zip: "06851",
      phone: "203-854-7700",
      hours: "Mon-Fri 8:30 AM - 4:30 PM",
      distance: "Norwalk City Center",
      website: "https://www.norwalkct.gov/",
      websiteLabel: "City of Norwalk",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "nrw-3",
      name: "Westport Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "154 Post Road East",
      city: "Westport",
      state: "CT",
      zip: "06880",
      phone: "203-227-1758",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Westport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "nrw-4",
      name: "Darien Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "25 Corbin Drive",
      city: "Darien",
      state: "CT",
      zip: "06820",
      phone: "203-655-1041",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Darien",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "nrw-5",
      name: "Wilton Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "201 Danbury Road",
      city: "Wilton",
      state: "CT",
      zip: "06897",
      phone: "203-762-8439",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Wilton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "nrw-6",
      name: "Stamford Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "421 Atlantic Street",
      city: "Stamford",
      state: "CT",
      zip: "06901",
      phone: "203-325-4611",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Stamford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Norwalk, CT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the Connecticut Passport Agency if I live in Norwalk?",
      a: "No — if you use Fast Passport Center, you don't need to visit the Connecticut Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Norwalk?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Norwalk, Westport, and Darien. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Norwalk?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Norwalk?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Norwalk?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Norwalk. We serve Norwalk and all of Fairfield County through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Norwalk for international travel?",
      a: "Westchester County Airport (HPN) is the closest at ~15 miles. LaGuardia Airport (LGA) is ~35 miles, JFK International is ~45 miles, and Newark (EWR) is ~50 miles. Many Norwalk residents use JFK, LGA, or Newark for international flights.",
    },
    {
      q: "How much does expedited passport service cost in Norwalk?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Norwalk?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Norwalk?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Norwalk?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Norwalk have a local passport agency?",
      a: "No, Norwalk does not have a regional passport agency. The nearest regional agency is the Connecticut Passport Agency at 10 Causeway St Suite 247, Boston, MA, approximately 45 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Norwalk", address: "various CVS locations in Norwalk", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Norwalk", address: "various Walgreens locations in Norwalk", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Norwalk", address: "various UPS Store locations in Norwalk", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Norwalk", address: "various FedEx Office locations in Norwalk", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Norwalk", address: "various Walmart locations in Norwalk", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "CT DMV — Norwalk",
      address: "540 Main Avenue, Norwalk, CT 06851",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: true,
    },
    {
      name: "CT DMV — Stamford",
      address: "137 Henry Street, Stamford, CT 06902",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HPN",
      name: "Westchester County Airport",
      distance: "~15 miles from Norwalk",
      address: "240 Airport Road, Purchase, NY 10577",
      note: "Closest airport to Norwalk with flights to major hubs. Perfect for domestic connections.",
      flightUrl: "https://www.westchestergov.com/airport",
      flightLabel: "Westchester County Airport",
      featured: true,
    },
    {
      code: "JFK",
      name: "JFK International Airport",
      distance: "~45 miles from Norwalk",
      address: "JFK Access Rd, Queens, NY 11430",
      note: "One of the busiest international airports in the world with flights to virtually every global destination.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK International",
      featured: false,
    },
    {
      code: "LGA",
      name: "LaGuardia Airport",
      distance: "~35 miles from Norwalk",
      address: "Queens, NY 11371",
      note: "Major domestic airport with extensive U.S. flight coverage. Convenient for Norwalk travelers.",
      flightUrl: "https://www.laguardiaairport.com/",
      flightLabel: "LaGuardia Airport",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Norwalk travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Norwalk residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Norwalk residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Norwalk residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Norwalk, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Norwalk residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Norwalk travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Norwalk residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Norwalk residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Norwalk residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Norwalk." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Norwalk." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Norwalk travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Norwalk travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Fairfield County team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Norwalk address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Norwalk-Specific Guidance", agency: "Generic — no Fairfield County or maritime expertise", fpc: "Full Norwalk/Fairfield County knowledge built in" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Norwalk", route: "/norwalk" },
    { name: "New Haven", route: "/new-haven" },
    { name: "Stamford", route: "/stamford" },
    { name: "Bridgeport", route: "/bridgeport" },
    { name: "Greenwich", route: "/greenwich" },
    { name: "Danbury", route: "/danbury" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Norwalk and all of Fairfield County as a remote courier service. We do not have a physical office in Norwalk, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "850 Canal Street, Stamford, CT 06902",
  fpcOfficeDistance: "Remote courier service — Norwalk, CT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Norwalk neighborhoods and surrounding Fairfield County communities. From downtown Norwalk to Westport, Darien to Wilton — we serve every Fairfield County community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Norwalk and all surrounding Fairfield County communities. Whether you're in downtown Norwalk or a nearby suburb, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Norwalk customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Norwalk travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Norwalk and Fairfield County travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Norwalk, CT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/connecticut",
  statePageName: "Connecticut Passport Services",
  dmvWebsite: "https://portal.ct.gov/DMV",
  dmvLabel: "Connecticut DMV — Official Website",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-heart-line", label: "Birth Certificates — Connecticut DPH", desc: "Request certified Connecticut birth certificates from the Department of Public Health Vital Records Office. Available by mail, online, or in person.", url: "https://portal.ct.gov/DPH/Vital-Records/Vital-Records-Home" },
    { icon: "ri-file-history-line", label: "Death Certificates — Connecticut DPH", desc: "Obtain certified Connecticut death certificates through the DPH Vital Records Office. Mail, online, and in-person options available.", url: "https://portal.ct.gov/DPH/Vital-Records/Vital-Records-Home" },
    { icon: "ri-heart-pulse-line", label: "Marriage Certificates — Norwalk Town Clerk", desc: "Request certified marriage certificates from the Norwalk Town Clerk's Office. Marriage records are maintained by the town where the ceremony took place.", url: "https://www.norwalkct.gov/town-clerk" },
    { icon: "ri-scales-line", label: "Divorce Records — Norwalk Superior Court", desc: "Obtain certified divorce decrees from the Norwalk Judicial District Superior Court. Divorce records are maintained by the court where the decree was granted.", url: "https://jud.ct.gov/" },

  ],
};

export default norwalkConfig;